/**
 * The browser's half of the login (SB-23; ADR-0004, ADR-0005).
 *
 * The session cookie is HttpOnly, so nothing here ever sees the token. What the browser does
 * know comes from the backends:
 *
 * - A 401 with code `token_expired` means the token's minutes ran out while the session lives.
 *   One `POST /api/auth/refresh` swaps it, and the request is sent again.
 * - Any other 401 the middleware sends (`not_authenticated`, `session_revoked`, `invalid_token`)
 *   means there is no session. The app goes to the sign-in page and keeps the route.
 * - `/api/auth/me` and the refresh say how many seconds the token has left. A link or an image
 *   the browser loads by itself (a plan PDF, a section image) cannot answer a 401 with a
 *   refresh, so the token is refreshed on activity before it runs out.
 *
 * Concurrent 401s share one refresh. The refresh goes through `fetch`, never through the axios
 * instance, so a refresh that fails cannot start another.
 */
import type {AxiosError, AxiosInstance, InternalAxiosRequestConfig} from 'axios';

export const SIGN_IN_PATH = '/login';

/** Why the app sent someone to the sign-in page, for the message it shows. */
export type SignInReason = 'session_expired';

type RefreshOutcome = 'ok' | 'ended' | 'failed';
type Listener = (reason: SignInReason | undefined) => void;

/** The codes the middleware sends when there is no session to refresh. */
const NO_SESSION = new Set(['not_authenticated', 'session_revoked', 'invalid_token']);
const ACTIVITY_THROTTLE_MS = 10_000;
const MAX_MARGIN_MS = 120_000;

const listeners = new Set<Listener>();
let refreshing: Promise<RefreshOutcome> | null = null;
let deadline: number | null = null;
let lifetime = 0;
let watching = false;
let lastCheck = 0;

/** Called when a request finds no session; returns the unsubscribe. */
export function onSignInRequired(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function signInRequired(reason?: SignInReason): void {
    listeners.forEach(listener => listener(reason));
}

/** How long the token has left, as /api/auth/me or a refresh reported it. */
export function noteTokenLifetime(seconds: number | null | undefined): void {
    if (typeof seconds === 'number' && seconds > 0) {
        deadline = Date.now() + seconds * 1000;
        lifetime = Math.max(lifetime, seconds * 1000);
    } else {
        deadline = null;
    }
}

/** One refresh at a time: every request waiting on a 401 shares it. */
export function refreshSession(): Promise<RefreshOutcome> {
    if (!refreshing) {
        refreshing = fetch('/api/auth/refresh', {method: 'POST', credentials: 'same-origin'})
            .then(async (response): Promise<RefreshOutcome> => {
                if (response.status === 401) return 'ended';
                if (!response.ok) return 'failed';
                const body = await response.json().catch(() => null);
                noteTokenLifetime(body?.expires_in);
                return 'ok';
            })
            .catch((): RefreshOutcome => 'failed')
            .finally(() => {
                refreshing = null;
            });
    }
    return refreshing;
}

/** What a 401 asks for: a refresh and a retry, the sign-in page, or nothing. */
async function answer401(code: string | undefined, canRetry: boolean): Promise<boolean> {
    if (code === 'token_expired' && canRetry) {
        const outcome = await refreshSession();
        if (outcome === 'ok') return true;
        if (outcome === 'ended') signInRequired('session_expired');
        return false;
    }
    if (code === 'token_expired' || code === 'session_revoked' || code === 'invalid_token') {
        signInRequired('session_expired');
    } else if (code && NO_SESSION.has(code)) {
        signInRequired();
    }
    return false;
}

type RetriableConfig = InternalAxiosRequestConfig & {_retriedAfterRefresh?: boolean};

/** The axios half: every request through the shared instance gets the 401 handling above. */
export function installAuthInterceptor(instance: AxiosInstance): void {
    instance.interceptors.response.use(undefined, async (error: AxiosError<{code?: string}>) => {
        const config = error.config as RetriableConfig | undefined;
        // The auth endpoints answer for themselves: a failed refresh must not start another.
        if (error.response?.status !== 401 || !config || String(config.url ?? '').startsWith('/api/auth/')) {
            throw error;
        }
        const canRetry = !config._retriedAfterRefresh;
        config._retriedAfterRefresh = true;
        if (await answer401(error.response.data?.code, canRetry)) return instance.request(config);
        throw error;
    });
}

/**
 * `fetch` for the streams that cannot go through axios, with the same 401 handling. A retry
 * sends the same request again, so the body must be one that can be sent twice: every stream
 * here sends a JSON string.
 */
export async function authFetch(input: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(input, init);
    if (response.status !== 401) return response;
    const code = await response.clone().json().then(
        (body: {code?: string} | null) => body?.code,
        () => undefined,
    );
    return (await answer401(code, true)) ? fetch(input, init) : response;
}

function margin(): number {
    return Math.min(MAX_MARGIN_MS, (lifetime || 3 * MAX_MARGIN_MS) / 3);
}

function onActivity(event: Event): void {
    if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastCheck < ACTIVITY_THROTTLE_MS) return;
    lastCheck = now;
    if (deadline !== null && now > deadline - margin()) {
        void refreshSession().then(outcome => {
            if (outcome === 'ended') signInRequired('session_expired');
        });
    }
}

const WINDOW_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'focus'] as const;

/**
 * Keep the token alive while someone is active, so a link opened after a pause still carries a
 * live one. Mouse movement counts: it comes before the click on the link.
 */
export function watchActivity(active: boolean): void {
    if (active === watching) return;
    watching = active;
    const method = active ? 'addEventListener' : 'removeEventListener';
    for (const name of WINDOW_EVENTS) window[method](name, onActivity, true);
    document[method]('visibilitychange', onActivity, true);
}

/** A path on this site to return to after sign-in, or the root (the server applies the same rule). */
export function safeNext(value: string | null | undefined): string {
    if (!value || value.length > 2000 || !value.startsWith('/') || value.startsWith('//')) return '/';
    // eslint-disable-next-line no-control-regex -- refusing a path with a control character in it is the point
    if (value.includes('\\') || /[\u0000-\u001f\u007f]/.test(value)) return '/';
    if (value === '/api' || value.startsWith('/api/') || value.startsWith('/api?') || value.startsWith(SIGN_IN_PATH)) {
        return '/';
    }
    return value;
}

/** Where the sign-in page is, with the way back. */
export function signInPath(next: string, reason?: SignInReason): string {
    const params = new URLSearchParams();
    const back = safeNext(next);
    if (back !== '/') params.set('next', back);
    if (reason) params.set('reason', reason);
    const query = params.toString();
    return query ? `${SIGN_IN_PATH}?${query}` : SIGN_IN_PATH;
}

/** The address a sign-in button links to: a page load, never an XHR. */
export function startUrl(provider: string, next: string): string {
    const back = safeNext(next);
    return `/api/auth/${provider}/start${back === '/' ? '' : `?next=${encodeURIComponent(back)}`}`;
}
